import { Document, Packer, Paragraph, TextRun, HeadingLevel, PageBreak, AlignmentType } from 'docx';
import { referenceService } from './referenceService.js';
import { settingsStore } from '../stores/settingsStore.js';
import { get } from 'svelte/store';

/**
 * Service for handling document exports to various formats
 */
export class ExportTextService {
    /**
     * Extracts the title from HTML content
     * @param {string} html - HTML content
     * @returns {string|null} Title text or null if not found
     */
    _extractTitle(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const titleElement = doc.querySelector('h1[data-type="title"]') || 
                           doc.querySelector('.title') ||
                           doc.querySelector('[data-block-type="title"]');
        return titleElement ? titleElement.textContent.trimEnd() : null;
    }

    /**
     * Creates reference map for export using unified reference service
     * @param {Document} doc - Parsed HTML document
     * @returns {Map<string, string>} Map of figure keys to export text
     */
    async _createFigureNumberMap(doc) {
        return await referenceService.createExportMap(doc);
    }

    /**
     * Converts HTML content to structured paragraphs
     * @param {string} html - HTML content to convert
     * @returns {Promise<Array>} Array of paragraph objects with their content and type
     */
    async _parseHtmlContent(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const titleElement = doc.querySelector('h1[data-type="title"]') || 
                           doc.querySelector('.title') ||
                           doc.querySelector('[data-block-type="title"]');
        
        // Remove the title if it exists
        if (titleElement) {
            titleElement.remove();
        }

        // Create export reference mapping
        const exportMap = await this._createFigureNumberMap(doc);

        const paragraphs = [];
        const elements = doc.body.children;
        
        // Track paragraph count for numbering
        let paragraphCount = 0;

        for (let element of elements) {
            // Skip completely empty elements (no text content at all)
            if (!element.textContent.trim()) continue;

            // Check if it's a heading
            if (element.tagName.toLowerCase().startsWith('h')) {
                paragraphs.push({
                    type: 'heading',
                    text: referenceService.processReferences(element, exportMap)
                });
            } else {
                // Check if this is a regenerative block
                const isRegenerativeBlock = element.matches('[data-block-type="regenerative-support"]');
                
                if (isRegenerativeBlock) {
                    // For regenerative blocks, split on <br> tags and create separate paragraphs
                    const processedText = referenceService.processReferences(element, exportMap);
                    const lines = processedText.split('\n').map(line => this._stripInlineComments(line).replace(/\]\]+/g, ''));
                    
                    // Create a paragraph for each line
                    lines.forEach(line => {
                        if (line || line === '') { // Include empty lines that might just have spaces
                            // Increment paragraph count for each non-empty paragraph
                            if (line.trim()) {
                                paragraphCount++;
                                const paddedNumber = paragraphCount.toString().padStart(4, '0');
                                
                                paragraphs.push({
                                    type: 'paragraph',
                                    text: line,
                                    paragraphNumber: `[${paddedNumber}]`
                                });
                            } else {
                                paragraphs.push({
                                    type: 'paragraph',
                                    text: line
                                });
                            }
                        }
                    });
                } else {
                    // Increment paragraph count for each non-empty paragraph
                    if (element.textContent.trim()) {
                        paragraphCount++;
                        const paddedNumber = paragraphCount.toString().padStart(4, '0');
                        
                        // Regular paragraph
                        paragraphs.push({
                            type: 'paragraph',
                            text: referenceService.processReferences(element, exportMap),
                            paragraphNumber: `[${paddedNumber}]`
                        });
                    } else {
                        paragraphs.push({
                            type: 'paragraph',
                            text: referenceService.processReferences(element, exportMap)
                        });
                    }
                }
            }
        }

        return paragraphs;
    }

    /**
     * Strip inline comments from text
     * @param {string} text - Text that may contain [[comments]]
     * @returns {string} Text with comments removed
     */
    _stripInlineComments(text) {
        // Use the same regex as inlineCommentMark.js for consistency
        // This regex matches [[...]] where ... can be any characters except the sequence ]]
        return text.replace(/\[\[(?:[^\]]|\](?!\]))*\]\]/g, '');
    }

    /**
     * Parses text and creates TextRun objects with custom styles applied
     * @param {string} text - Text that may contain custom style delimiters
     * @param {number} fontSize - Font size in half-points (default 24 = 12pt)
     * @param {string} fontColor - Font color hex (default "000000")
     * @returns {Array<TextRun>} Array of TextRun objects with appropriate styling
     */
    _parseStyledText(text, fontSize = 24, fontColor = "000000") {
        const settings = get(settingsStore);
        const { style1, style2 } = settings.personalization.customStyles;

        const textRuns = [];
        let remainingText = text;

        // Create regex patterns for both styles
        // For {{ }}, we need to escape the braces
        const style1Pattern = /\{\{([^}]*)\}\}/g;
        // For \\ \\, we need to escape the backslashes
        const style2Pattern = /\\\\([^\\]*)\\\\/g;

        // Find all matches for both patterns
        const matches = [];

        let match;
        while ((match = style1Pattern.exec(text)) !== null) {
            matches.push({
                start: match.index,
                end: match.index + match[0].length,
                content: match[1],
                style: style1
            });
        }

        while ((match = style2Pattern.exec(text)) !== null) {
            matches.push({
                start: match.index,
                end: match.index + match[0].length,
                content: match[1],
                style: style2
            });
        }

        // Sort matches by start position
        matches.sort((a, b) => a.start - b.start);

        // If no matches, return simple text run
        if (matches.length === 0) {
            return [new TextRun({
                text: text,
                size: fontSize,
                color: fontColor,
                font: "Times New Roman"
            })];
        }

        // Build text runs with styled segments
        let currentPos = 0;

        for (const match of matches) {
            // Add text before the match
            if (match.start > currentPos) {
                textRuns.push(new TextRun({
                    text: text.substring(currentPos, match.start),
                    size: fontSize,
                    color: fontColor,
                    font: "Times New Roman"
                }));
            }

            // Add styled text
            const hexColor = match.style.color.replace('#', '');
            textRuns.push(new TextRun({
                text: match.content,
                size: fontSize,
                color: hexColor,
                font: "Times New Roman",
                bold: match.style.bold,
                italics: match.style.italic,
                underline: match.style.underline ? {} : undefined
            }));

            currentPos = match.end;
        }

        // Add remaining text after last match
        if (currentPos < text.length) {
            textRuns.push(new TextRun({
                text: text.substring(currentPos),
                size: fontSize,
                color: fontColor,
                font: "Times New Roman"
            }));
        }

        return textRuns;
    }


    /**
     * Creates a regular paragraph
     * @param {string} text - Text content
     * @param {string} paragraphNumber - Paragraph number in format [0001]
     * @returns {Paragraph} Word paragraph
     */
    _createParagraph(text, paragraphNumber) {
        const children = [];

        // Add the paragraph number if it exists
        if (paragraphNumber) {
            children.push(
                new TextRun({
                    text: paragraphNumber + ' ',
                    size: 24, // 12pt font (24 half-points)
                    color: "000000", // Black color
                    font: "Times New Roman",
                    bold: true
                })
            );
        }

        // Parse the text for custom styles and add the resulting text runs
        const styledTextRuns = this._parseStyledText(text, 24, "000000");
        children.push(...styledTextRuns);

        return new Paragraph({
            children: children,
            spacing: { line: 600 } // 0.5 line spacing (1200 = double spacing, 600 = 0.5 spacing)
        });
    }

    /**
     * Creates a heading paragraph with specified styling
     * @param {string} text - Heading text
     * @returns {Paragraph} Formatted heading paragraph
     */
    _createHeading(text) {
        return new Paragraph({
            heading: HeadingLevel.HEADING_2, // This will create proper Word heading style
            alignment: AlignmentType.CENTER,
            spacing: { 
                before: 240, 
                after: 240,
                line: 600 // 0.5 line spacing (1200 = double spacing, 600 = 0.5 spacing)
            },
            children: [
                new TextRun({
                    text: text.toUpperCase().trimEnd(),
                    size: 24, // 12pt font (24 half-points)
                    color: "000000", // Black color
                    font: "Times New Roman",
                    bold: true // Make headings bold
                })
            ]
        });
    }

    /**
     * Converts parsed paragraphs to Word document elements
     * Respects the actual section headings from the spec
     * @param {Array} specParagraphs - Parsed specification paragraphs
     * @returns {Array} Array of Word document elements
     */
    _convertParagraphsToWordElements(specParagraphs) {
        const elements = [];

        for (const item of specParagraphs) {
            if (item.type === 'heading') {
                elements.push(this._createHeading(item.text));
            } else {
                elements.push(this._createParagraph(item.text, item.paragraphNumber));
            }
        }

        return elements;
    }

    /**
     * Exports the combined content to a Word document
     * @param {string} specHtml - HTML content from the spec editor
     * @param {string} claimsText - Text content from the claims editor
     * @returns {Promise<Blob>} Word document as a Blob
     */
    async exportToWord(specHtml, claimsText) {
        try {
            console.log('Starting Word export:', {
                specHtmlLength: specHtml?.length || 0,
                claimsTextLength: claimsText?.length || 0
            });
            
            // Extract title and parse content
            const title = this._extractTitle(specHtml);
            const specParagraphs = await this._parseHtmlContent(specHtml);
            
            console.log('Parsed content:', {
                title: title,
                specParagraphsCount: specParagraphs.length,
                claimsLinesCount: claimsText.split('\n').length
            });

            // Convert spec paragraphs to Word elements, respecting actual section structure
            const specElements = this._convertParagraphsToWordElements(specParagraphs);

            const doc = new Document({
                defaultFontName: "Times New Roman",
                sections: [{
                    properties: {},
                    children: [
                        // Title (if exists)
                        ...(title ? [this._createHeading(title)] : []),

                        // Spec sections - use actual sections from the editor
                        ...specElements,
                        
                        // Page break before claims
                        new Paragraph({
                            children: [new PageBreak()]
                        }),
                        
                        // Claims section
                        this._createHeading("Claims"),
                        ...claimsText.split('\n')
                            .filter(line => !line.trimStart().startsWith('#')) // Filter out inline comment lines
                            .map(line => line.trimEnd()) // Only trim trailing whitespace
                            .map(line => this._createParagraph(line))
                    ],
                }],
            });

            console.log('Document created, generating blob...');
            
            // Generate and return the document as a blob
            const blob = await Packer.toBlob(doc);
            
            console.log('Word document exported successfully:', {
                blobSize: blob.size,
                blobType: blob.type
            });
            
            if (blob.size === 0) {
                throw new Error('Generated Word document is empty. Please check the document content.');
            }
            
            return blob;
        } catch (error) {
            console.error('Error exporting to Word:', error);
            throw new Error(`Failed to export Word document: ${error.message}`);
        }
    }
}

export const exportTextService = new ExportTextService();